import type { JsonObject } from "@/lib/api/types";

import type { HmiCanvasDocument, HmiElement } from "../schema/hmi-canvas.schema";
import type { HmiBinding } from "../schema/hmi-bindings.schema";
import { builtinHmiSymbols } from "../registry/builtin-symbols";
import { builtinHmiSvgAssets } from "../registry/builtin-svg-assets";
import { HMI_DOCUMENT_KIND, HMI_DOCUMENT_VERSION } from "../schema/hmi-canvas.schema";

export type HmiDashboardType = "classic" | "hmi";

const HMI_DEFAULT_LINE_COLOR = "#1f6f54";

function nextId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function camelToSnake(value: string): string {
  return value.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
}

function snakeToCamel(value: string): string {
  return value.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());
}

export function toApiShape(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => toApiShape(item));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [camelToSnake(key), toApiShape(entry)]),
    );
  }
  return value;
}

export function fromApiShape(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => fromApiShape(item));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [snakeToCamel(key), fromApiShape(entry)]),
    );
  }
  return value;
}

export function createDefaultHmiDocument(): HmiCanvasDocument {
  return {
    kind: HMI_DOCUMENT_KIND,
    version: HMI_DOCUMENT_VERSION,
    canvas: {
      width: 1600,
      height: 900,
      background: {
        type: "solid",
        color: "#f6fbf7",
      },
      grid: {
        enabled: true,
        size: 16,
        snap: true,
      },
    },
    elements: [],
    bindings: [],
    symbols: builtinHmiSymbols,
    assets: builtinHmiSvgAssets.map((asset) => ({ ...asset })),
    variables: [],
    metadata: {
      workflow: {
        publicationStatus: "draft",
      },
      revisions: [],
    },
  };
}

export function cloneHmiDocument(document: HmiCanvasDocument): HmiCanvasDocument {
  return JSON.parse(JSON.stringify(document)) as HmiCanvasDocument;
}

export function toDashboardLayout(document: HmiCanvasDocument): JsonObject {
  return toApiShape(document) as unknown as JsonObject;
}

export function createDefaultElement(type: HmiElement["type"], zIndex: number, options?: { symbolDefinitionId?: string }): HmiElement {
  const id = nextId(type);
  const base = {
    id,
    type,
    name: type.replace(/_/g, " "),
    x: 120,
    y: 120,
    width: 180,
    height: 72,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    zIndex,
    style: {},
    rules: [],
  };

  switch (type) {
    case "text":
      return { ...base, type, text: "Label" };
    case "value":
      return { ...base, type, placeholder: "--" };
    case "stat_card":
      return { ...base, type, width: 220, height: 120, label: "Stat", placeholder: "--" };
    case "data_table":
      return { ...base, type, width: 360, height: 220, table: { columns: ["Timestamp", "Value"], rowLimit: 8 } };
    case "rectangle":
      return { ...base, type, cornerRadius: 10 };
    case "circle":
      return { ...base, type, width: 96, height: 96 };
    case "line":
      return { ...base, type, width: 220, height: 16, points: [0, 8, 220, 8], style: { stroke: HMI_DEFAULT_LINE_COLOR } };
    case "arrow":
      return {
        ...base,
        type,
        width: 220,
        height: 36,
        points: [8, 18, 212, 18],
        style: { stroke: HMI_DEFAULT_LINE_COLOR },
        arrow: { direction: "right" },
      };
    case "flow_line":
      return {
        ...base,
        type,
        width: 240,
        height: 72,
        style: { stroke: HMI_DEFAULT_LINE_COLOR },
        flow: {
          shape: "horizontal",
          direction: "forward",
          animate: true,
          speed: 48,
          stripeLength: 18,
          stripeGap: 10,
          stripeColor: "#ffffff",
          lineWidth: 12,
          turnRatio: 0.65,
        },
      };
    case "trend_chart":
      return {
        ...base,
        type,
        width: 320,
        height: 180,
        chart: {
          mode: "compact",
          showLegend: false,
          showAxes: true,
          showGrid: true,
          lineWidth: 3,
        },
      };
    case "gauge":
      return {
        ...base,
        type,
        width: 220,
        height: 220,
        gauge: {
          min: 0,
          warningLow: 25,
          warningHigh: 75,
          max: 100,
          showTicks: true,
        },
      };
    case "symbol_instance":
      return {
        ...base,
        type,
        width: 200,
        height: 200,
        symbol: {
          definitionId: options?.symbolDefinitionId ?? "builtin.hvac.fan",
          definitionVersion: 1,
          overrides: {},
        },
      };
    case "svg_asset":
      return {
        ...base,
        type,
        width: 260,
        height: 180,
        asset: {
          assetId: builtinHmiSvgAssets[0]!.id,
          preserveAspectRatio: "xMidYMid meet",
        },
      };
    case "image_asset":
      return {
        ...base,
        type,
        width: 320,
        height: 220,
        asset: {
          assetId: undefined,
          fit: "contain",
          alt: "Uploaded HMI image",
        },
      };
    case "command_button":
      return {
        ...base,
        type,
        width: 180,
        height: 60,
        command: {
          label: "Send command",
          action: "trigger",
          payload: {},
          requireConfirm: true,
          variant: "primary",
        },
      };
    case "command_toggle":
      return {
        ...base,
        type,
        width: 220,
        height: 80,
        toggle: {
          label: "Toggle",
          action: "set_output",
          onLabel: "On",
          offLabel: "Off",
          onPayload: { state: "on" },
          offPayload: { state: "off" },
          requireConfirm: true,
        },
      };
    case "setpoint":
      return {
        ...base,
        type,
        width: 260,
        height: 100,
        setpoint: {
          label: "Setpoint",
          action: "set_setpoint",
          defaultValue: 22,
          min: 0,
          max: 100,
          step: 1,
          unit: "°C",
          requireConfirm: true,
          payload: {},
        },
      };
  }
}

export function createDefaultBinding(element: HmiElement): HmiBinding | null {
  const targetMap: Partial<Record<HmiElement["type"], HmiBinding["target"]>> = {
    value: "value",
    stat_card: "value",
    data_table: "chart_series",
    trend_chart: "chart_series",
    gauge: "gauge_value",
    command_toggle: "status",
    setpoint: "value",
  };
  const target = targetMap[element.type];
  if (!target) {
    return null;
  }
  return {
    id: nextId("binding"),
    elementId: element.id,
    target,
    source: {
      kind: "static",
      value: null,
    },
    rules: [],
  };
}
